<template>
  <div>
    <apexchart type="area" height="120" :options="chartOptions" :series="series" />
  </div>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts'

export default {
  name: 'ProfitChart',
  components: { apexchart: VueApexCharts },

  props: {
    // ожидается: { pnl: number[], dates: string[], deposit?: number }
    chart_data: { type: Object, required: true },
    // перекрывает chart_data.deposit при передаче
    deposit: { type: Number, default: null },
    // 'equity' — рисуем баланс; 'roi' — рисуем ROI в %
    mode: { type: String, default: 'equity' } // 'equity' | 'roi'
  },

  data() {
    return {
      series: [],
      chartOptions: {}
    }
  },

  computed: {
    initialDeposit() {
      const d = this.deposit ?? this.chart_data?.deposit ?? 0
      return Number(d) || 0
    },

    cumulativePnl() {
      const pnl = Array.isArray(this.chart_data?.pnl) ? this.chart_data.pnl : []
      const acc = []
      let sum = 0
      for (const v of pnl) {
        sum += Number(v) || 0
        acc.push(sum)
      }
      return acc
    },

    equityData() {
      return this.cumulativePnl.map(v => this.initialDeposit + v)
    },

    roiData() {
      if (this.initialDeposit <= 0) return this.cumulativePnl.map(() => 0)
      return this.equityData.map(v => ((v - this.initialDeposit) / this.initialDeposit) * 100)
    },

    finalGrowth() {
      const data = this.mode === 'roi' ? this.roiData : this.equityData
      if (!data.length) return 0
      // для ROI точка отсчёта — 0%, для equity — initialDeposit
      return data[data.length - 1] - (this.mode === 'roi' ? 0 : this.initialDeposit)
    }
  },

  watch: {
    chart_data: { deep: true, handler() { this.rebuild() } },
    deposit() { this.rebuild() },
    mode() { this.rebuild() }
  },

  mounted() {
    this.rebuild()
  },

  methods: {
    rebuild() {
      const categories = Array.isArray(this.chart_data?.dates) ? this.chart_data.dates : []
      const data = this.mode === 'roi' ? this.roiData : this.equityData
      const isUp = this.finalGrowth >= 0
      const color = isUp ? '#5ec776' : '#ff5353'

      this.series = [{
        name: this.mode === 'roi' ? 'ROI, %' : this.$t('balance'),
        data
      }]

      this.chartOptions = {
        theme: { mode: this.$theme?.chartTheme ?? 'dark' },
        chart: {
          height: 120,
          type: 'area',
          zoom: { enabled: false },
          toolbar: { show: false },
          parentHeightOffset: 0,
          offsetX: 0,
          offsetY: 0
        },
        stroke: { curve: 'smooth', width: 2 },
        fill: {
          type: 'gradient',
          gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0, stops: [0, 100] }
        },
        tooltip: {
          enabled: true,
          y: {
            formatter: (val) => this.mode === 'roi'
                ? `${val.toFixed(2)}%`
                : Number(val).toFixed(2)
          }
        },
        colors: [color],
        markers: {
          size: 3,
          colors: [color],
          strokeColors: [color],
          strokeWidth: 2,
          hover: { size: 7 }
        },
        xaxis: {
          categories,
          labels: { show: false },
          axisBorder: { show: false },
          axisTicks: { show: false },
          title: { text: undefined }
        },
        yaxis: {
          show: false,
          labels: { show: false },
          title: { text: undefined }
        },
        grid: {
          show: false,
          padding: { left: 0, right: 0, top: 0, bottom: 0 }
        },
        dataLabels: { enabled: false }
      }
    }
  }
}
</script>

<style scoped>
</style>
